import { useEffect, useState } from "react";
import { ShieldAlert, Users, ArrowUpRight, UserPlus, HelpCircle, Loader2 } from "lucide-react";
import { api } from "@/api/client";

// Wallet & Monetization Hub — Earnings / Payout-Gate tracker (S20).
// Real data via GET /api/rewards/payout-gate: earned coins (earnedCredits @ $0.10),
// distinct follower count, and the site-wide paid-subscriber milestone (→ 4,000).
// Cash-out settlement stays DISPLAY-ONLY / escrow-locked (Stage 8).
export default function EarnedLedgerTracker() {
  const [gate, setGate] = useState(null);
  const [showStatus, setShowStatus] = useState(false);

  useEffect(() => {
    api.get("/rewards/payout-gate").then((r) => setGate(r.data)).catch(() => setGate({ error: true }));
  }, []);

  if (!gate) {
    return <div className="grid place-items-center py-10" data-testid="earned-loading"><Loader2 className="w-5 h-5 text-[#FFCC00] animate-spin" /></div>;
  }
  if (gate.error) {
    return (
      <div className="w-full bg-zinc-950 border border-red-500/30 p-4 rounded-xl flex items-start gap-2.5" data-testid="earned-error">
        <ShieldAlert className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
        <p className="text-zinc-300 text-[10px] font-medium leading-relaxed">Couldn't load your earnings right now. Pull down or reopen the hub to retry.</p>
      </div>
    );
  }

  const earned = Number(gate.earned_coins ?? 0);
  const audValue = Number(gate.aud_value ?? earned * 0.10).toFixed(2);
  const followers = Number(gate.followers ?? 0);
  const followerThreshold = Number(gate.follower_threshold ?? 100);
  const followerGate = !!gate.follower_gate_cleared;
  const followersRemaining = Math.max(0, followerThreshold - followers);
  const globalSubs = Number(gate.global_subs ?? 0);
  const targetSubs = Number(gate.global_subs_target ?? 4000);
  const subsRemaining = Math.max(0, targetSubs - globalSubs);
  const progressPct = Math.min(100, Math.floor((globalSubs / targetSubs) * 100));

  return (
    <div className="w-full text-white select-none" data-testid="earned-ledger-tracker">
      {/* 3-second headline — the primary outcome number, front and centre */}
      <div className="w-full bg-[#98FFB6] border-2 border-black rounded-xl p-4 mb-4 text-center shadow-[3px_3px_0px_#000]" data-testid="earned-headline">
        <p className="text-[10px] text-black/70 font-black tracking-widest uppercase">💰 Money You're Earning Right Now</p>
        <p className="text-black font-mono font-black text-4xl leading-none mt-1.5" data-testid="earned-headline-aud">${audValue}</p>
        <p className="text-black/60 text-[10px] font-bold font-mono mt-1">{earned} coins earned so far</p>
      </div>
      {/* Earned wallet */}
      <div className="w-full bg-[#0A0A0A] border border-zinc-900 rounded-xl p-4 mb-4">
        <span className="text-[10px] text-zinc-400 font-black tracking-widest uppercase block mb-1">Your Coin Balance</span>
        <div className="flex justify-between items-center bg-black/40 border border-zinc-800 p-3 rounded-lg">
          <div>
            <h4 className="text-white text-xs font-black uppercase">Earned Coin Balance</h4>
            <p className="text-zinc-400 text-[9px] font-medium mt-0.5">Tracking your real-world platform interaction rewards</p>
          </div>
          <div className="text-right">
            <span className="text-[#FFCC00] font-mono font-black text-base block" data-testid="earned-balance">{earned} PC</span>
            <span className="text-zinc-400 text-[10px] font-bold font-mono">${audValue} AUD</span>
          </div>
        </div>
        <button
          type="button"
          data-testid="earned-request-cashout"
          onClick={() => setShowStatus(true)}
          className="w-full h-11 bg-zinc-900 border border-zinc-800 hover:border-[#FFCC00] text-white font-bold text-xs uppercase rounded-xl flex items-center justify-center gap-2 mt-3 transition-colors"
        >
          Request Cash-Out Settlement <ArrowUpRight className="w-3.5 h-3.5 text-zinc-500" />
        </button>
      </div>

      {/* Gate response */}
      {showStatus && (
        <div className="mb-4" data-testid="earned-gate-status">
          {!followerGate ? (
            <div className="w-full bg-zinc-950 border border-red-500/30 p-3 rounded-xl flex items-start gap-2.5" data-testid="earned-gate-followers">
              <UserPlus className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <div>
                <h5 className="text-red-400 text-[10px] font-black tracking-widest uppercase">Creator Gate Locked</h5>
                <p className="text-zinc-300 text-[9px] font-medium leading-relaxed mt-0.5">
                  Payout requires a minimum of <span className="text-white font-bold">{followerThreshold} followers</span>. You need <span className="text-red-400 font-mono font-bold">{followersRemaining}</span> more. Share your reels to grow your base!
                </p>
              </div>
            </div>
          ) : (
            <div className="w-full bg-zinc-950 border border-amber-500/30 p-3 rounded-xl flex items-start gap-2.5" data-testid="earned-gate-austrac">
              <ShieldAlert className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <h5 className="text-white text-[10px] font-black tracking-widest uppercase">AUSTRAC Regulatory Hold</h5>
                <p className="text-zinc-300 text-[9px] font-medium leading-relaxed mt-0.5">
                  Follower threshold verified (<span className="text-emerald-400 font-bold">{followers}</span>). Payout channels remain in system escrow until the site-wide active-subscriber milestone clears compliance.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Network progress */}
      <div className="w-full bg-[#0A0A0A] border border-zinc-900 rounded-xl p-4">
        <div className="flex justify-between items-end mb-2">
          <div>
            <span className="text-zinc-400 text-[9px] font-black tracking-wider uppercase block">Network Horizon</span>
            <h5 className="text-white text-xs font-black uppercase mt-0.5">P2P Settlement Activation</h5>
          </div>
          <div className="text-right">
            <span className="text-[#00E5FF] font-mono font-black text-xs block" data-testid="earned-progress-pct">{progressPct}% Active</span>
            <span className="text-zinc-400 font-mono text-[9px]">{globalSubs}/{targetSubs} Users</span>
          </div>
        </div>
        <div className="w-full h-2 bg-black rounded-full overflow-hidden border border-zinc-800 mb-3">
          <div className="h-full bg-gradient-to-r from-[#A855F7] via-[#00E5FF] to-[#FFCC00] transition-all duration-1000" style={{ width: `${progressPct}%` }} />
        </div>
        <div className="bg-black/60 border border-zinc-900 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#00E5FF]" />
            <span className="text-zinc-300 text-[10px] font-bold">Required Remaining Subscribers:</span>
          </div>
          <span className="text-[#00E5FF] font-mono font-black text-sm" data-testid="earned-subs-remaining">{subsRemaining}</span>
        </div>
        <div className="flex items-center justify-center gap-1 text-[8px] text-zinc-500 font-bold uppercase tracking-wider pt-3">
          <HelpCircle className="w-2.5 h-2.5" /> Building a transparent, legally ironclad creator super app
        </div>
      </div>
    </div>
  );
}
